import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { Container } from '@mui/material';

// Components
import Header from './components/Header';
import ProtectedRoute from './components/ProtectedRoute';

// Pages
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
// FMLA Patient Pages
import PatientDashboard from './pages/PatientDashboard';
import BookingDashboardPage from './pages/BookingDashboardPage';
import ServiceSelectionPage from './pages/ServiceSelectionPage';
import AppointmentsPage from './pages/AppointmentsPage';
import AssessmentPortalPage from './pages/AssessmentPortalPage';
import DocumentsPage from './pages/DocumentsPage';
import UploadDocumentPage from './pages/UploadDocumentPage';
// ProvidersPage removed - using auto-assignment workflow
import ProfilePage from './pages/ProfilePage';
// FMLA Provider Pages
import ProviderDashboard from './pages/ProviderDashboard';
import ProviderAvailabilityPage from './pages/ProviderAvailabilityPage';

// Auth Context
import { useAuth } from './contexts/AuthContext';

// Component to redirect to appropriate dashboard based on user type
function DashboardRedirect() {
  const { user } = useAuth();
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  if (user.userType === 'provider') {
    return <Navigate to="/provider/dashboard" />;
  } else {
    return <Navigate to="/patient/dashboard" />;
  }
}

// Inner component that handles routing
function AppRoutes() {
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading && location.pathname !== '/auth/callback') {
    return <div>Loading...</div>;
  }

  return (
    <>
      <Header />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Routes>
            <Route path="/" element={!isAuthenticated ? <LoginPage /> : <DashboardRedirect />} />
            <Route path="/login" element={!isAuthenticated ? <LoginPage /> : <DashboardRedirect />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/forgot-password" element={!isAuthenticated ? <ForgotPasswordPage /> : <DashboardRedirect />} />
            
            {/* Legacy dashboard redirect */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <DashboardRedirect />
              </ProtectedRoute>
            } />
            
            {/* Patient Routes */}
            <Route path="/patient/dashboard" element={
              <ProtectedRoute>
                <PatientDashboard />
              </ProtectedRoute>
            } />
            
            {/* Redirect legacy booking flow to service selection */}
            <Route path="/book-appointment" element={
              <ProtectedRoute>
                <Navigate to="/services" replace />
              </ProtectedRoute>
            } />
            
            {/* Payment required redirect - sends unpaid users to services */}
            <Route path="/payment-required" element={
              <ProtectedRoute>
                <Navigate to="/services" replace />
              </ProtectedRoute>
            } />
            
            {/* Service Selection - publicly accessible so new users can see pricing */}
            <Route path="/services" element={<ServiceSelectionPage />} />
            
            {/* Booking Dashboard - for paid users to select appointment slots */}
            <Route path="/dashboard/booking" element={
              <ProtectedRoute>
                <BookingDashboardPage />
              </ProtectedRoute>
            } />
            
            <Route path="/appointments" element={
              <ProtectedRoute>
                <AppointmentsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/appointments/schedule" element={
              <ProtectedRoute>
                <Navigate to="/dashboard/booking" replace />
              </ProtectedRoute>
            } />
            
            <Route path="/assessments/portal/:appointmentId" element={
              <ProtectedRoute>
                <AssessmentPortalPage />
              </ProtectedRoute>
            } />
            
            {/* Document Management */}
            <Route path="/documents" element={
              <ProtectedRoute>
                <DocumentsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/documents/upload" element={
              <ProtectedRoute>
                <UploadDocumentPage />
              </ProtectedRoute>
            } />
            
            {/* Provider Discovery - REMOVED: Auto-assignment workflow */}
            
            {/* User Profile */}
            <Route path="/profile" element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            } />
            
            {/* Provider Routes */}
            <Route path="/provider/dashboard" element={
              <ProtectedRoute>
                <ProviderDashboard />
              </ProtectedRoute>
            } />
            
            <Route path="/provider/availability" element={
              <ProtectedRoute>
                <ProviderAvailabilityPage />
              </ProtectedRoute>
            } />
            
            <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Container>
    </>
  );
}

function App() {
  const location = useLocation();
  const { isAuthenticated, isLoading, login, logout } = useAuth();

  useEffect(() => {
    // Skip authentication check on callback page
    if (location.pathname === '/auth/callback') {
      return;
    }

    // Only check auth state once on app load, not on every navigation
    if (isAuthenticated) {
      return;
    }

    const checkAuthState = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        
        // Store the access token in localStorage for axios
        const session = await Auth.currentSession();
        const accessToken = session.getAccessToken().getJwtToken();
        localStorage.setItem('aws-token', accessToken);
        
        // Let AuthContext handle user data extraction and merging
        // Don't duplicate the logic here to avoid overwriting complete data
      } catch (error) {
        // Clear token if authentication fails
        localStorage.removeItem('aws-token');
        logout();
      }
    };

    // Add timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      logout();
    }, 5000);

    checkAuthState().finally(() => {
      clearTimeout(timeoutId);
    });
  }, []); // Remove location.pathname dependency to prevent re-auth on navigation

  return <AppRoutes />;
}

export default App;
